import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Sitemap } from '../../components/Sitemap';
import { NextPage } from '../../components/NextPage';
import { BookIcon, VolumeIcon, CheckCircleIcon, PlayIcon, PauseIcon, LightbulbIcon, StarIcon, BrainIcon, PenIcon, HeadphonesIcon, ClockIcon, XIcon } from 'lucide-react';
const LESSON_DATA = {
  id: 2,
  title: {
    en: 'Basic Greetings',
    ar: 'التحيات الأساسية'
  },
  description: {
    en: 'Learn essential greetings and introductions',
    ar: 'تعلم التحيات والمقدمات الأساسية'
  },
  duration: '20 minutes',
  durationAr: '20 دقيقة',
  sections: [{
    id: 'formal-greetings',
    title: {
      en: 'Formal Greetings',
      ar: 'التحيات الرسمية'
    },
    phrases: [{
      en: 'Good morning',
      ar: 'صباح الخير',
      context: 'Used until noon',
      contextAr: 'تستخدم حتى الظهر'
    }, {
      en: 'Good afternoon',
      ar: 'مساء الخير',
      context: 'Used from noon until evening',
      contextAr: 'تستخدم من الظهر حتى المساء'
    }, {
      en: 'Good evening',
      ar: 'مساء الخير',
      context: 'Used in the evening',
      contextAr: 'تستخدم في المساء'
    }, {
      en: 'How do you do?',
      ar: 'كيف حالك؟',
      context: 'Very formal first-time greeting',
      contextAr: 'تحية رسمية جداً للمرة الأولى'
    }]
  }, {
    id: 'casual-greetings',
    title: {
      en: 'Casual Greetings',
      ar: 'التحيات غير الرسمية'
    },
    phrases: [{
      en: 'Hello',
      ar: 'مرحباً',
      context: 'General greeting',
      contextAr: 'تحية عامة'
    }, {
      en: 'Hi',
      ar: 'أهلاً',
      context: 'Informal greeting',
      contextAr: 'تحية غير رسمية'
    }, {
      en: "What's up?",
      ar: 'ما الجديد؟',
      context: 'Very casual greeting',
      contextAr: 'تحية غير رسمية جداً'
    }, {
      en: 'How are you?',
      ar: 'كيف حالك؟',
      context: 'Common greeting question',
      contextAr: 'سؤال تحية شائع'
    }]
  }],
  practice: [{
    question: {
      en: 'Which greeting is appropriate in the morning?',
      ar: 'أي تحية مناسبة في الصباح؟'
    },
    options: [{
      en: 'Good morning',
      ar: 'صباح الخير',
      correct: true
    }, {
      en: 'Good evening',
      ar: 'مساء الخير',
      correct: false
    }, {
      en: 'Good night',
      ar: 'تصبح على خير',
      correct: false
    }, {
      en: 'Good afternoon',
      ar: 'مساء الخير',
      correct: false
    }]
  }, {
    question: {
      en: 'How would you casually greet a friend?',
      ar: 'كيف تحيي صديقاً بشكل غير رسمي؟'
    },
    options: [{
      en: 'Hi',
      ar: 'أهلاً',
      correct: true
    }, {
      en: 'How do you do?',
      ar: 'كيف حالك؟',
      correct: false
    }, {
      en: 'Good evening, sir',
      ar: 'مساء الخير، سيدي',
      correct: false
    }, {
      en: 'Pleased to meet you',
      ar: 'سعيد بلقائك',
      correct: false
    }]
  }],
  tips: [{
    en: 'Always match your greeting to the time of day',
    ar: 'طابق دائماً تحيتك مع وقت اليوم'
  }, {
    en: "Use more formal greetings with people you don't know well",
    ar: 'استخدم تحيات أكثر رسمية مع الأشخاص الذين لا تعرفهم جيداً'
  }, {
    en: 'Smile when greeting someone to appear friendly',
    ar: 'ابتسم عند تحية شخص ما لتبدو ودوداً'
  }]
};
export function BasicGreetingsLesson() {
  const {
    isArabic
  } = useLanguage();
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
  const [showResults, setShowResults] = useState(false);
  const playAudio = (text: string) => {
    if (playingAudio === text) {
      window.speechSynthesis.cancel();
      setPlayingAudio(null);
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.onend = () => setPlayingAudio(null);
    setPlayingAudio(text);
    window.speechSynthesis.speak(utterance);
  };
  const handleAnswerSelect = (questionIndex: number, optionIndex: number) => {
    if (showResults) return;
    setSelectedAnswers({
      ...selectedAnswers,
      [questionIndex]: optionIndex
    });
  };
  const checkAnswers = () => {
    setShowResults(true);
  };
  const resetPractice = () => {
    setSelectedAnswers({});
    setShowResults(false);
  };
  const getCorrectAnswers = () => {
    return LESSON_DATA.practice.reduce((count, question, index) => {
      const selectedAnswer = selectedAnswers[index];
      if (selectedAnswer !== undefined && question.options[selectedAnswer].correct) {
        return count + 1;
      }
      return count;
    }, 0);
  };
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3518">
      <Sitemap currentPage={{
      en: `Lesson ${LESSON_DATA.id}: ${LESSON_DATA.title.en}`,
      ar: `الدرس ${LESSON_DATA.id}: ${LESSON_DATA.title.ar}`
    }} breadcrumbs={[{
      label: {
        en: 'Learn',
        ar: 'تعلم'
      },
      href: '/learn'
    }, {
      label: {
        en: 'Course 1',
        ar: 'الدورة 1'
      },
      href: '/learn/course/1'
    }]} data-id="element-3519" />
      <div className="max-w-4xl mx-auto" data-id="element-3520">
        <div className="mb-12" data-id="element-3521">
          <div className="flex items-center gap-4 mb-6" data-id="element-3522">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-3523">
              <BookIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3524" />
            </div>
            <div data-id="element-3525">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-3526">
                {isArabic ? LESSON_DATA.title.ar : LESSON_DATA.title.en}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-3527">
                {isArabic ? LESSON_DATA.description.ar : LESSON_DATA.description.en}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6" data-id="element-3528">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-3529">
              <ClockIcon className="w-5 h-5" data-id="element-3530" />
              <span data-id="element-3531">
                {isArabic ? LESSON_DATA.durationAr : LESSON_DATA.duration}
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-3532">
              <BrainIcon className="w-5 h-5" data-id="element-3533" />
              <span data-id="element-3534">
                {LESSON_DATA.sections.length} {isArabic ? 'أقسام' : 'sections'}
              </span>
            </div>
          </div>
        </div>
        {LESSON_DATA.sections.map(section => <div key={section.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-8" data-id="element-3535">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-3536">
              {isArabic ? section.title.ar : section.title.en}
            </h2>
            <div className="space-y-4" data-id="element-3537">
              {section.phrases.map((phrase, index) => <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-3538">
                  <div className="flex items-center justify-between mb-2" data-id="element-3539">
                    <div data-id="element-3540">
                      <div className="font-medium text-gray-900 dark:text-white text-lg" data-id="element-3541">
                        {phrase.en}
                      </div>
                      <div className="text-blue-600 dark:text-blue-400 text-lg" dir="rtl" data-id="element-3542">
                        {phrase.ar}
                      </div>
                    </div>
                    <button onClick={() => playAudio(phrase.en)} className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" data-id="element-3543">
                      {playingAudio === phrase.en ? <PauseIcon className="w-5 h-5" data-id="element-3544" /> : <VolumeIcon className="w-5 h-5" data-id="element-3545" />}
                    </button>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-3546">
                    {isArabic ? phrase.contextAr : phrase.context}
                  </div>
                </div>)}
            </div>
          </div>)}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 mb-8" data-id="element-3547">
          <div className="flex items-center gap-3 mb-6" data-id="element-3548">
            <PenIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3549" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3550">
              {isArabic ? 'تمارين' : 'Practice'}
            </h2>
          </div>
          <div className="space-y-8" data-id="element-3551">
            {LESSON_DATA.practice.map((question, qIndex) => <div key={qIndex} className="space-y-4" data-id="element-3552">
                <p className="text-lg text-gray-900 dark:text-white" data-id="element-3553">
                  {isArabic ? question.question.ar : question.question.en}
                </p>
                <div className="grid grid-cols-1 gap-3" data-id="element-3554">
                  {question.options.map((option, oIndex) => <button key={oIndex} onClick={() => handleAnswerSelect(qIndex, oIndex)} className={`
                        p-4 rounded-lg text-left transition-colors
                        ${selectedAnswers[qIndex] === oIndex ? showResults ? option.correct ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-600'}
                      `} data-id="element-3555">
                      <div className="flex justify-between items-center" data-id="element-3556">
                        <div data-id="element-3557">
                          <div className="font-medium" data-id="element-3558">{option.en}</div>
                          <div className="text-sm opacity-75" data-id="element-3559">{option.ar}</div>
                        </div>
                        {showResults && (selectedAnswers[qIndex] === oIndex ? option.correct ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-3560" /> : <XIcon className="w-5 h-5 text-red-500" data-id="element-3561" /> : null)}
                      </div>
                    </button>)}
                </div>
              </div>)}
          </div>
          <div className="mt-6" data-id="element-3562">
            {showResults ? <div data-id="element-3563">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-4" data-id="element-3564">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-3565">
                    {isArabic ? 'نتائجك' : 'Your Results'}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300" data-id="element-3566">
                    {isArabic ? `لقد أجبت على ${getCorrectAnswers()} من ${LESSON_DATA.practice.length} بشكل صحيح` : `You got ${getCorrectAnswers()} out of ${LESSON_DATA.practice.length} correct`}
                  </p>
                </div>
                <button onClick={resetPractice} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-3567">
                  {isArabic ? 'حاول مرة أخرى' : 'Try Again'}
                </button>
              </div> : <button onClick={checkAnswers} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" disabled={Object.keys(selectedAnswers).length < LESSON_DATA.practice.length} data-id="element-3568">
                {isArabic ? 'تحقق من إجاباتك' : 'Check Answers'}
              </button>}
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-8 mb-8" data-id="element-3569">
          <div className="flex items-center gap-3 mb-6" data-id="element-3570">
            <LightbulbIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-3571" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-3572">
              {isArabic ? 'نصائح مفيدة' : 'Helpful Tips'}
            </h2>
          </div>
          <ul className="space-y-4" data-id="element-3573">
            {LESSON_DATA.tips.map((tip, index) => <li key={index} className="flex items-start gap-3" data-id="element-3574">
                <StarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-1" data-id="element-3575" />
                <span className="text-gray-800 dark:text-gray-200" data-id="element-3576">
                  {isArabic ? tip.ar : tip.en}
                </span>
              </li>)}
          </ul>
        </div>
        <NextPage currentPage={LESSON_DATA.id} totalPages={5} nextPageUrl={`/learn/course/1/lesson/${LESSON_DATA.id + 1}`} prevPageUrl={`/learn/course/1/lesson/${LESSON_DATA.id - 1}`} nextTitle={{
        en: 'Numbers and Counting',
        ar: 'الأرقام والعد'
      }} nextDescription={{
        en: 'Learn to count and use numbers in English',
        ar: 'تعلم العد واستخدام الأرقام باللغة الإنجليزية'
      }} data-id="element-3577" />
      </div>
    </div>;
}